import React, {
  useCallback,
  useMemo,
  useState,
  useRef
} from 'react'
import PropTypes from 'prop-types'
import qs from 'query-string'
import {
  find,
  flatten,
  head,
  isEmpty,
  length,
  merge,
  path,
  pathOr,
  pluck,
  propEq,
  propOr
} from 'ramda'
import { useLocation } from 'react-router-dom'
import classNames from 'classnames'

import {
  navigationGa,
  seasonHouseId
} from '../../lib/analytics/ga'
import { customDimensions } from '../../lib/analytics/custom-dimensions'
import { parseDecimalInt, getResponsiveSeasonShowCount } from '../../lib/utils'
import EmblaCarousel from '../emblaCarousel'
import Episode from './episode'
import styles from './seasons.css'

const getPlaybackSelection = ({
  playbackInfoData,
  seasons,
  season: deeplinkSeasonNumber,
  episode: deeplinkEpisodeNumber
}) => {
  let selectedEpisodeId = pathOr(null, ['playbackInfo', 'episodeToPlay'], playbackInfoData)
  // replace selectedEpisodeId with the episode from the deep link
  // if the season is not matched, highlight the default episode
  // (selectedEpisodeId - episodeToPlay in playbackInfo)
  // if only season is provided and no episode is provided, highlight the 1st episode of that season
  // if the episode is not matched, highlight the 1st episode of that season
  if (deeplinkSeasonNumber) {
    const deeplinkSeason = seasons.find(
      season => season.seasonNumber === deeplinkSeasonNumber
    )
    if (deeplinkSeason) {
      const deeplinkSeasonFirstEpisode = deeplinkSeason.episodes.find(
        episode => !!episode
      )
      selectedEpisodeId = deeplinkSeasonFirstEpisode
        ? deeplinkSeasonFirstEpisode.id
        : selectedEpisodeId

      if (deeplinkEpisodeNumber) {
        const deeplinkEpisode = deeplinkSeason.episodes.find(
          episode => episode.episodeNumber === deeplinkEpisodeNumber
        )
        selectedEpisodeId = deeplinkEpisode ? deeplinkEpisode.id : selectedEpisodeId
      }
    }
  }

  return ({
    playbackDetails: pathOr(null, ['playbackInfo'], playbackInfoData),
    selectedEpisodeId
  })
}

const getInitialSeasonSelected = ({ seasons, selectedEpisodeId }) => {
  let seasonSelected
  if (selectedEpisodeId) {
    seasons.forEach((season) => {
      season.episodes.forEach((episode) => {
        if (episode.id === selectedEpisodeId) {
          seasonSelected = episode.seasonNumber
        }
      })
    })
  }

  return seasonSelected || pathOr(1, ['0', 'seasonNumber'], seasons)
}

function Seasons({
  playbackInfoData,
  seasons,
  series,
  screenWidth
}) {
  const location = useLocation()
  // season and episode from deep link (e.g. season=1&episode=1)
  // to highlight the season and episode in the list
  const deepLinkProps = useMemo(
    () => {
      const { season, episode } = qs.parse(location.search || '')

      return {
        season: parseDecimalInt(season),
        episode: parseDecimalInt(episode)
      }
    },
    [location]
  )
  const playbackSelection = useMemo(
    () => getPlaybackSelection({
      playbackInfoData,
      seasons,
      ...deepLinkProps
    }),
    [deepLinkProps, playbackInfoData, seasons]
  )
  const {
    playbackDetails,
    selectedEpisodeId
  } = playbackSelection
  const [seasonSelected, setSeasonSelected] = useState(() => getInitialSeasonSelected({
    seasons,
    selectedEpisodeId
  }))
  const onSeasonSelected = useCallback((seasonNumber) => {
    let selectedEpisode = {}
    if (playbackDetails) {
      const episodeToPlay = path(['episodeToPlay'], playbackDetails)
      selectedEpisode = episodeToPlay && find(propEq('id', episodeToPlay), flatten(pluck('episodes', series.seasons)))
    }

    navigationGa({
      action: 'Change Season',
      label: `${series.title} S${seasonNumber}`,
      [customDimensions.ShowTitle]: series.title,
      [customDimensions.SeasonNumber]: seasonSelected,
      [customDimensions.SeriesTitleHouseId]: series.ldId,
      [customDimensions.SeasonHouseId]:
          selectedEpisode && !isEmpty(selectedEpisode) && seasonHouseId(
            series.seasons.find(season => season.seasonNumber === selectedEpisode.seasonNumber)
          ),
      [customDimensions.EpisodeHouseId]: selectedEpisode && selectedEpisode.ldId
    })
    setSeasonSelected(seasonNumber)
  }, [
    playbackDetails,
    seasonSelected,
    series
  ])

  if (!length(seasons)) {
    return null
  }

  // get the season selected item or, if doesnt exist season 1, get the first season on the list
  const fullSeasonSelected = find(
    season => season.seasonNumber === seasonSelected,
    seasons
  ) || head(seasons)
  const playbackInfoArray = propOr([], 'items', playbackDetails)
  const highlightEpisodeId = selectedEpisodeId || propOr(
    '',
    'episodeToPlay',
    playbackDetails
  )
  const isWatchable = pathOr(false, ['watchable'], playbackDetails)
  const slidesToShow = getResponsiveSeasonShowCount(screenWidth, 1)
  const initialSeasonIndexRef = useRef(
    Math.max(0, seasons.findIndex(season => season.seasonNumber === seasonSelected))
  )

  return (
    <div className={styles.seasons}>
      <div
        className={classNames(styles.seasonsSelector, {
          [styles.oneSeasonSelector]: seasons.length <= 1
        })}
      >
        <EmblaCarousel
          startSlideIndex={initialSeasonIndexRef.current}
          slidesToShow={slidesToShow}
          activeSlideCount={seasons.length}
          emblaViewportCustomStyle={styles.emblaViewport}
        >
          {seasons.map(season => (
            <div
              key={season.id || season.seasonNumber}
              className={classNames(
                styles.emblaSlide,
                {
                  [styles.textActive]: seasonSelected === season.seasonNumber
                }
              )}
              role="button"
              tabIndex={0}
              onClick={() => onSeasonSelected(season.seasonNumber)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  onSeasonSelected(season.seasonNumber)
                }
              }}
            >
              {`Season ${season.seasonNumber}`}
            </div>
          ))}
        </EmblaCarousel>
      </div>

      <div className={styles.episodes}>
        {
          fullSeasonSelected.episodes.map((episode) => {
            let playbackInfoItem = playbackInfoArray.find(
              playbackInfo => playbackInfo.contentItemId === episode.id
            )
            // add the series watchable field on the episode playbackinfoitem
            playbackInfoItem = merge(playbackInfoItem, {
              watchable: isWatchable
            })
            return (
              <Episode
                key={episode.id}
                episode={episode}
                playbackInfo={playbackInfoItem}
                highlight={
                  highlightEpisodeId
                    ? episode.id === highlightEpisodeId
                    : episode.id === pathOr(null, ['0', 'episodes', '0', 'id'], seasons)
                }
              />
            )
          })
        }
      </div>
    </div>
  )
}

Seasons.propTypes = {
  series: PropTypes.oneOfType([PropTypes.object]).isRequired,
  seasons: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    seasonNumber: PropTypes.number
  })).isRequired,
  playbackInfoData: PropTypes.oneOfType([PropTypes.object]),
  screenWidth: PropTypes.number
}

Seasons.defaultProps = {
  playbackInfoData: null,
  screenWidth: window.innerWidth
}

export default Seasons
